import { createSelector } from "@reduxjs/toolkit";
import { selectAllRecipes, selectRecipesTotal } from "./slices/recipesSlice";
import { selectAllRecentlyViewed } from "./slices/recentViewsSlice";
import { RootState } from "./rootReducer";
import { RecentlyViewedRecipe, Recipe } from "../models/recipe";
import {
  selectAllRecipeCollections,
  selectRecipeCollectionByRecipeId,
  selectRecipeCollectionsByCollectionId,
} from "./slices/recipeCollections";
import { selectAllCollections } from "./slices/collectionsSlice";
import { CollectionSummary } from "../models/recipe-collection";
import { recipeImageUri } from "../utils/recipeImage";
import { compareRecipeTitles } from "../utils/recipeSort";
import { collectionImageUri } from "../utils/collectionImage";
import { selectInProgressCookingProgresses } from "./slices/cookingProgressSlice";
import { selectAllMethodSteps } from "./slices/methodStepsSlice";
import { selectAllCookingHistory } from "./slices/cookingHistorySlice";

export const selectRecentlyViewed = createSelector(
  [selectAllRecipes, selectAllRecentlyViewed, (_: RootState, limit?: number) => limit],
  (recipes, recentlyViewed, limit): RecentlyViewedRecipe[] => {
    // Sort by most recently viewed first
    const sorted = [...recentlyViewed].sort((a, b) =>
      b.viewedAt.localeCompare(a.viewedAt),
    );

    // Apply limit if provided
    const limited = limit ? sorted.slice(0, limit) : sorted;

    // Map to full recipe objects
    const recipeMap = new Map(recipes.map((r) => [r.id, r]));
    return limited
      .map((rv) => {
        const recipe = recipeMap.get(rv.recipeId);
        if (!recipe) return null;

        // Add viewedAt property value
        return { ...recipe, viewedAt: rv.viewedAt };
      })
      .filter(Boolean) as RecentlyViewedRecipe[];
  },
);

// Recipes in a collection, in the collection's manual (drag-to-reorder) order.
// This is the canonical order used everywhere a collection's recipes are shown
// (collection screen "Custom order", the meal-plan picker, etc.).
export const selectRecipesByCollection = createSelector(
  [
    (state: RootState, collectionId: string) =>
      selectRecipeCollectionsByCollectionId(state, collectionId),
    selectAllRecipes,
  ],
  (recipeCollections, recipes): Recipe[] => {
    const orderByRecipe = new Map(
      recipeCollections.map((rc) => [rc.recipeId, rc.sortOrder ?? 0]),
    );
    return recipes
      .filter((r) => orderByRecipe.has(r.id))
      .sort((a, b) => {
        const diff = orderByRecipe.get(a.id)! - orderByRecipe.get(b.id)!;
        return diff !== 0 ? diff : compareRecipeTitles(a.title, b.title);
      });
  },
);

export const selectCollectionIdsByRecipeId = createSelector(
  [selectRecipeCollectionByRecipeId],
  (recipeCollections) => recipeCollections.map((rc) => rc.collectionId),
);

export const selectCollectionSummaries = createSelector(
  [selectAllCollections, selectAllRecipeCollections, selectAllRecipes],
  (collections, recipeCollections, recipes): CollectionSummary[] => {
    return collections.map((collection) => {
      // Get all recipeIds for this collection
      const recipeIds = recipeCollections
        .filter((rc) => rc.collectionId === collection.id)
        .map((rc) => rc.recipeId);

      const recipeCount = recipeIds.length;

      // Resolve image — use the collection's own image (incl. a not-yet-uploaded
      // local pick / offline cache) if set, else the first recipe (alphabetically)
      // with an image.
      let resolvedImage = collectionImageUri(collection);

      if (!resolvedImage) {
        const matchingRecipes = recipes
          .filter((r) => recipeIds.includes(r.id) && !!recipeImageUri(r))
          .sort((a, b) => compareRecipeTitles(a.title, b.title));

        resolvedImage = recipeImageUri(matchingRecipes[0]);
      }

      return {
        ...collection,
        recipeCount,
        resolvedImage,
      };
    });
  },
);

// Cap the "Continue Cooking" row so it never becomes an endless carousel.
const CONTINUE_COOKING_LIMIT = 12;

export const selectRecipesWithInProgressCook = createSelector(
  [selectAllRecipes, selectInProgressCookingProgresses, selectAllMethodSteps],
  (recipes, inProgress, methodSteps) => {
    const recipeById = new Map(recipes.map((r) => [r.id, r]));
    const stepCounts = methodSteps.reduce<Record<string, number>>((acc, ms) => {
      acc[ms.recipeId] = (acc[ms.recipeId] ?? 0) + 1;
      return acc;
    }, {});

    const seen = new Set<string>();
    return [...inProgress]
      // Most recently cooked first.
      .sort((a, b) => +new Date(b.lastActiveAt) - +new Date(a.lastActiveAt))
      // Skip sessions whose recipe was deleted, and collapse duplicate sessions
      // per recipe (keeping the most recent, since the list is already sorted).
      .filter((cp) => {
        if (!recipeById.has(cp.recipeId) || seen.has(cp.recipeId)) return false;
        seen.add(cp.recipeId);
        return true;
      })
      .slice(0, CONTINUE_COOKING_LIMIT)
      .map((cp) => ({
        recipe: recipeById.get(cp.recipeId)!,
        currentStep: cp.currentStep,
        totalSteps: stepCounts[cp.recipeId] ?? 0,
      }));
  },
);

// recipeId -> the latest cook time (ISO). Powers the "Most recently cooked"
// sort. A recipe can be cooked more than once, so we keep the newest timestamp.
export const selectLastCookedByRecipe = createSelector(
  [selectAllCookingHistory],
  (history) => {
    const map: Record<string, string> = {};
    for (const h of history) {
      if (h.completedAt && (!map[h.recipeId] || h.completedAt > map[h.recipeId])) {
        map[h.recipeId] = h.completedAt;
      }
    }
    return map;
  },
);

// recipeId -> total number of times cooked. Powers the "Most cooked" sort (and
// mirrors the count shown in the home "Cook it again" section).
export const selectCookCountByRecipe = createSelector(
  [selectAllCookingHistory],
  (history) => {
    const map: Record<string, number> = {};
    for (const h of history) {
      if (h.deletedAt || !h.completedAt) continue;
      map[h.recipeId] = (map[h.recipeId] ?? 0) + 1;
    }
    return map;
  },
);

// Every cook of a recipe, newest first — powers the recipe's cooking-history page.
export const selectCookingHistoryByRecipeId = createSelector(
  [selectAllCookingHistory, (_: RootState, recipeId: string) => recipeId],
  (history, recipeId) =>
    history
      .filter((h) => h.recipeId === recipeId && !h.deletedAt)
      .sort((a, b) => (b.completedAt ?? "").localeCompare(a.completedAt ?? "")),
);

// The user's most-cooked recipes (with cook count + last-cooked time), for the
// home "Cook it again" section. Ties broken by most-recently cooked.
export const selectMostCookedRecipes = (limit: number) =>
  createSelector([selectAllCookingHistory, selectAllRecipes], (history, recipes) => {
    const stats = new Map<string, { count: number; last: string }>();
    for (const h of history) {
      if (h.deletedAt || !h.completedAt) continue;
      const s = stats.get(h.recipeId) ?? { count: 0, last: "" };
      s.count += 1;
      if (h.completedAt > s.last) s.last = h.completedAt;
      stats.set(h.recipeId, s);
    }
    const byId = new Map(recipes.map((r) => [r.id, r]));
    return [...stats.entries()]
      .map(([id, s]) => ({ recipe: byId.get(id), count: s.count, lastCookedAt: s.last }))
      .filter((x): x is { recipe: Recipe; count: number; lastCookedAt: string } => !!x.recipe)
      .sort((a, b) => b.count - a.count || b.lastCookedAt.localeCompare(a.lastCookedAt))
      .slice(0, limit);
  });

export const selectHasRecipes = createSelector(selectRecipesTotal, (total) => total > 0);

// Whether this store is still waiting on its FIRST sync to bring data down —
// true on a fresh launch/login before any sync has completed, so the home can
// show a loader instead of flashing the empty state. Once a sync has run (or
// we're offline/errored with nothing to wait for), this is false and callers
// should trust selectHasRecipes. lastSyncAt is null until the first completed
// sync; pulled rows are applied before status flips off "syncing".
export const selectInitialSyncPending = (state: RootState): boolean => {
  const { status, lastSyncAt, isOnline } = state.sync;
  if (status === "error") return false;
  return status === "syncing" || (lastSyncAt === null && isOnline);
};
